/**
 * When the SPA talks to auth via localhost or a private gateway hop, the server often only sees
 * RFC1918 / loopback addresses and cannot geo-locate. We optionally call a small HTTPS endpoint
 * from the *browser* (so it resolves the user's egress IP), cache briefly, and send it as
 * `X-Client-Public-IP` on auth requests. The auth-service only trusts this when the TCP-derived
 * IP is still private/local (never overrides a real public connection IP).
 */
const CACHE_KEY = 'iq_hrms_client_public_ip_v1';
const CACHE_TTL_MS = 10 * 60 * 1000;
const IPIFY_URL = 'https://api.ipify.org?format=json';

/** Single flight so parallel `authRequest` calls share one ipify fetch. */
let inFlightLookup: Promise<string | null> | null = null;

interface CacheEntry {
  ip: string;
  exp: number;
}

function isDisabledViaEnv(): boolean {
  try {
    return import.meta.env.VITE_DISABLE_CLIENT_PUBLIC_IP === 'true';
  } catch {
    return false;
  }
}

function isUsablePublicIp(ip: string): boolean {
  const t = ip.trim();
  if (!t) return false;
  if (t.includes(':')) {
    const l = t.toLowerCase();
    if (l === '::1' || l.startsWith('fe80:') || /^f[cd][0-9a-f]:/.test(l)) return false;
    return true;
  }
  if (/^(127\.|10\.|192\.168\.)/.test(t)) return false;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(t)) return false;
  if (t === '0.0.0.0') return false;
  return /^(25[0-5]|2[0-4]\d|[01]?\d\d?)(\.(25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/.test(t);
}

export async function getOrFetchClientPublicIp(): Promise<string | null> {
  if (typeof window === 'undefined' || isDisabledViaEnv()) return null;

  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) {
      const entry = JSON.parse(raw) as CacheEntry;
      if (typeof entry.ip === 'string' && entry.exp > Date.now() && isUsablePublicIp(entry.ip)) {
        return entry.ip.trim();
      }
    }
  } catch {
    /* ignore cache */
  }

  if (inFlightLookup) return inFlightLookup;

  inFlightLookup = (async (): Promise<string | null> => {
    try {
      const ctrl = new AbortController();
      const timer = window.setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch(IPIFY_URL, { signal: ctrl.signal, credentials: 'omit' });
      window.clearTimeout(timer);
      if (!res.ok) return null;
      const data = (await res.json()) as { ip?: string };
      const ip = typeof data.ip === 'string' ? data.ip.trim() : '';
      if (!ip || !isUsablePublicIp(ip)) return null;
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ip, exp: Date.now() + CACHE_TTL_MS }));
      } catch {
        /* private mode, quota, etc. */
      }
      return ip;
    } catch {
      return null;
    } finally {
      inFlightLookup = null;
    }
  })();

  return inFlightLookup;
}
